import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { checkinAPI } from '../../services/api';
import { apiErrorMessage } from '../../utils/apiError';

type CheckInState = 'loading' | 'ready' | 'confirming' | 'success' | 'already' | 'error' | 'email-fallback';

interface BookingSummary {
  booking_id: number;
  booking_time: string;
  booking_date: string;
  duration_minutes: number;
  clinician_first_name: string;
  already_checked_in: boolean;
}

const CheckIn: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [state, setState] = useState<CheckInState>(token ? 'loading' : 'email-fallback');
  const [booking, setBooking] = useState<BookingSummary | null>(null);
  const [error, setError] = useState<string>('');
  const [email, setEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [lookupToken, setLookupToken] = useState<string>('');

  // Auto-resolve token on mount
  useEffect(() => {
    if (!token) return;

    const resolveToken = async () => {
      try {
        const data = await checkinAPI.resolve(token);
        setBooking(data);
        if (data.already_checked_in) {
          setState('already');
        } else {
          setState('ready');
        }
      } catch (err: any) {
        const detail = apiErrorMessage(err, 'Invalid or expired check-in link.');
        setError(detail);
        setState('email-fallback');
      }
    };

    resolveToken();
  }, [token]);

  const handleConfirm = async () => {
    setState('confirming');
    try {
      const result = token
        ? await checkinAPI.confirm({ token })
        : await checkinAPI.confirm({ lookup_token: lookupToken });
      if (result.already_checked_in) {
        setState('already');
      } else {
        setState('success');
      }
    } catch (err: any) {
      const detail = apiErrorMessage(err, 'Unable to check in. Please see the front desk.');
      setError(detail);
      setState('error');
    }
  };

  const handleEmailLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setEmailLoading(true);
    setError('');
    try {
      const data = await checkinAPI.lookup(email);
      setLookupToken(data.lookup_token);
      setBooking({
        booking_id: data.booking_id,
        booking_time: data.booking_time,
        booking_date: data.booking_date,
        duration_minutes: data.duration_minutes,
        clinician_first_name: data.patient_first_name,
        already_checked_in: data.already_checked_in,
      });
      if (data.already_checked_in) {
        setState('already');
      } else {
        setState('ready');
      }
    } catch (err: any) {
      const detail = apiErrorMessage(err, 'No upcoming appointments found.');
      setError(detail);
    } finally {
      setEmailLoading(false);
    }
  };

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remaining = minutes % 60;
    return remaining > 0 ? `${hours} hr ${remaining} min` : `${hours} hr`;
  };

  // In the email-fallback flow, clinician_first_name is actually the patient's name
  // (kiosk privacy — clinician name is not exposed). Show "With" only for token flow.
  const isTokenFlow = !!token;

  return (
    <div className="min-h-screen bg-clinic-surface flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-clinic-sm border-b border-clinic-primary-lighter">
        <div className="max-w-lg mx-auto px-4 py-4 text-center">
          <img
            src="/logos/logo-primary.png"
            alt="Clinic Studios"
            className="h-8 mx-auto"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">

          {/* Loading state */}
          {state === 'loading' && (
            <div className="bg-white rounded-card shadow-clinic-md p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-clinic-primary mx-auto mb-4"></div>
              <p className="text-clinic-muted font-body">Verifying your check-in link...</p>
            </div>
          )}

          {/* Ready to check in */}
          {state === 'ready' && booking && (
            <div className="bg-white rounded-card shadow-clinic-md p-8 text-center">
              <h1 className="font-display text-2xl font-bold text-clinic-dark mb-2">
                {isTokenFlow ? 'Welcome!' : `Hi ${booking.clinician_first_name}!`}
              </h1>
              <p className="text-clinic-muted font-body mb-6">
                Ready to check in for your appointment?
              </p>

              {/* Appointment summary */}
              <div className="bg-clinic-surface rounded-button p-4 mb-6 text-left">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs font-semibold text-clinic-primary uppercase tracking-wide">Date</p>
                    <p className="text-sm font-semibold text-clinic-dark">{booking.booking_date}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-clinic-primary uppercase tracking-wide">Time</p>
                    <p className="text-sm font-semibold text-clinic-dark">{booking.booking_time}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-clinic-primary uppercase tracking-wide">Duration</p>
                    <p className="text-sm font-semibold text-clinic-dark">{formatDuration(booking.duration_minutes)}</p>
                  </div>
                  {isTokenFlow && (
                    <div>
                      <p className="text-xs font-semibold text-clinic-primary uppercase tracking-wide">With</p>
                      <p className="text-sm font-semibold text-clinic-dark">{booking.clinician_first_name}</p>
                    </div>
                  )}
                </div>
              </div>

              <button
                onClick={handleConfirm}
                className="w-full py-4 px-6 bg-clinic-primary hover:bg-clinic-primary-dark text-white font-body font-semibold text-lg rounded-button transition-colors disabled:opacity-50"
              >
                I'm Here
              </button>
            </div>
          )}

          {/* Confirming state */}
          {state === 'confirming' && (
            <div className="bg-white rounded-card shadow-clinic-md p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-clinic-primary mx-auto mb-4"></div>
              <p className="text-clinic-muted font-body">Checking you in...</p>
            </div>
          )}

          {/* Success */}
          {state === 'success' && (
            <div className="bg-white rounded-card shadow-clinic-md p-8 text-center">
              <div className="w-20 h-20 bg-clinic-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircleIcon className="h-12 w-12 text-clinic-cream" />
              </div>
              <h1 className="font-display text-2xl font-bold text-clinic-dark mb-2">
                You're All Checked In!
              </h1>
              <p className="text-clinic-muted font-body mb-2">
                Your clinician has been notified.
              </p>
              <p className="text-clinic-muted font-body text-sm">
                Please have a seat in the waiting area.
              </p>
            </div>
          )}

          {/* Already checked in */}
          {state === 'already' && (
            <div className="bg-white rounded-card shadow-clinic-md p-8 text-center">
              <div className="w-20 h-20 bg-clinic-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircleIcon className="h-12 w-12 text-clinic-cream" />
              </div>
              <h1 className="font-display text-2xl font-bold text-clinic-dark mb-2">
                Already Checked In
              </h1>
              <p className="text-clinic-muted font-body">
                You've already checked in for this appointment. Your clinician has been notified.
              </p>
            </div>
          )}

          {/* Error */}
          {state === 'error' && (
            <div className="bg-white rounded-card shadow-clinic-md p-8 text-center">
              <div className="w-16 h-16 bg-clinic-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <ExclamationCircleIcon className="h-10 w-10 text-clinic-accent" />
              </div>
              <h1 className="font-display text-xl font-bold text-clinic-dark mb-2">
                Unable to Check In
              </h1>
              <p className="text-clinic-muted font-body mb-4">
                {error}
              </p>
              <p className="text-sm text-clinic-muted">
                Please check with the front desk for assistance.
              </p>
            </div>
          )}

          {/* Email fallback */}
          {state === 'email-fallback' && (
            <div className="bg-white rounded-card shadow-clinic-md p-8">
              <h1 className="font-display text-2xl font-bold text-clinic-dark text-center mb-2">
                Check In
              </h1>
              <p className="text-clinic-muted font-body text-center mb-6">
                Enter your email to find your appointment.
              </p>

              {error && (
                <div className="bg-clinic-accent/10 border border-clinic-accent/20 rounded-button p-3 mb-4">
                  <p className="text-sm text-clinic-accent text-center">{error}</p>
                </div>
              )}

              <form onSubmit={handleEmailLookup}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  autoFocus
                  className="w-full px-4 py-3 border border-clinic-primary-lighter rounded-input font-body text-clinic-dark placeholder-clinic-muted focus:outline-none focus:ring-2 focus:ring-clinic-primary focus:border-transparent text-lg mb-4"
                />
                <button
                  type="submit"
                  disabled={emailLoading || !email.trim()}
                  className="w-full py-4 px-6 bg-clinic-primary hover:bg-clinic-primary-dark text-white font-body font-semibold text-lg rounded-button transition-colors disabled:opacity-50"
                >
                  {emailLoading ? 'Looking up...' : 'Find My Appointment'}
                </button>
              </form>
            </div>
          )}

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-clinic-primary-lighter">
        <div className="max-w-lg mx-auto px-4 py-4 text-center">
          <p className="text-xs text-clinic-muted font-body">
            &copy; {new Date().getFullYear()} Clinic Studios. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CheckIn;
